/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { loadStripe } from "@stripe/stripe-js";
import { createCheckoutSession, getCoupons } from "../core/_requests";
import { useAuth } from "../../../auth";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY!);

interface FeatureDetail {
  id: number;
  name: string;
  description: string;
}

interface PlanDetail {
  amount: number;
  validity: string;
  validityCount: number;
  type: number; // 1=Monthly, 2=Yearly
  isActive: boolean;
}

interface Plan {
  id: string;
  name: string;
  description?: string;
  featuresDetails: FeatureDetail[];
  plan_details: PlanDetail[];
}

interface Coupon {
  stripeCouponId: string;
  id: string;
  name: string;
  couponValueType: number; // 1=Percentage, 2=Fixed amount
  amount: number;
  planId: string;
}

interface LocationState {
  plan: Plan;
  selectedDetail: PlanDetail;
  walletBalance: number;
  couponId?: string;
}

const SubscriptionDetailsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const localId = localStorage.getItem("chefId");
console.log("Local Stored Chef ID:", localId);
  const chefId =localId || currentUser?.uuid;

  const { plan, selectedDetail, walletBalance = 0, couponId } =
    (location.state || {}) as LocationState;

  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState<Coupon | null>(null);
  const [allCoupons, setAllCoupons] = useState<Coupon[]>([]);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const [walletInputAmount, setWalletInputAmount] = useState(0);
  const [paymentType, setPaymentType] = useState(2); // 1=Wallet, 2=Card, 3=Partial
  const [discountedAmount, setDiscountedAmount] = useState<number | null>(null);
const [userSelectedPayment, setUserSelectedPayment] = useState(false);

  // Fetch coupons
  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        const response = await getCoupons(2);
        setAllCoupons(response.data || []);
      } catch (error) {
        console.error("Error fetching coupons:", error);
      }
    };
    fetchCoupons();
  }, []);

  // Apply couponId discount on load
  useEffect(() => {
    if (!selectedDetail || !allCoupons.length) return;

    if (couponId) {
      const coupon = allCoupons.find((c) => c.name === couponId);
      if (coupon) {
        applyCoupon(coupon);
      }
    }
  }, [couponId, allCoupons, selectedDetail]);

  // Calculate wallet amounts when payment type or discount changes
  useEffect(() => {
    if (!selectedDetail) return;

    const finalAmount = discountedAmount ?? selectedDetail.amount;

    if (paymentType === 1) {
      setWalletInputAmount(Math.min(walletBalance, finalAmount));
    } else if (paymentType === 3) {
      setWalletInputAmount(Math.min(walletBalance, finalAmount));
    } else {
      setWalletInputAmount(0);
    }
  }, [walletBalance, selectedDetail, discountedAmount, paymentType]);

  // Apply coupon
  const applyCoupon = (coupon: Coupon) => {
    if (!selectedDetail) return;

    let discountAmount = 0;
    if (coupon.couponValueType === 1) {
      discountAmount = (selectedDetail.amount * coupon.amount) / 100;
    } else if (coupon.couponValueType === 2) {
      discountAmount = coupon.amount;
    }

    const finalAmount = Math.max(selectedDetail.amount - discountAmount, 0);

    setAppliedCoupon(coupon);
    setDiscountedAmount(finalAmount);

    if (!userSelectedPayment) {
  if (walletBalance >= finalAmount) setPaymentType(1);
  else if (walletBalance > 0) setPaymentType(3);
  else setPaymentType(2);
}

  };

  if (!plan || !selectedDetail) {
    return (
      <div className="container mt-5 text-center">
        <h5>No plan selected.</h5>
        <button
          className="btn btn-outline-secondary mt-3"
          onClick={() => navigate("/subscription/list")}
        >
          Back to Plans
        </button>
      </div>
    );
  }

  const originalAmount = Number(selectedDetail.amount) || 0;

  let discount = 0;
  if (appliedCoupon) {
    discount =
      appliedCoupon.couponValueType === 1
        ? (originalAmount * appliedCoupon.amount) / 100
        : appliedCoupon.amount;
  }

  const total = discountedAmount ?? originalAmount;
  const features = plan.featuresDetails || [];

  const handleApplyCoupon = () => {
    const coupon = allCoupons.find(
      (c) => c.name.toUpperCase() === couponCode.trim().toUpperCase()
    
    );
    if (!coupon) {
      toast.error("Invalid coupon code");
      setAppliedCoupon(null);
      setDiscountedAmount(null);
      return;



    }

    applyCoupon(coupon);
    toast.success(`Coupon ${coupon.name} applied!`);
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setDiscountedAmount(null);
    setCouponCode("");

    if (walletBalance > 0) {
      if (walletBalance >= originalAmount) setPaymentType(1);
      else setPaymentType(3);
    } else setPaymentType(2);
  };

  // Create checkout session

const handleSubscribe = async (): Promise<string | void> => {
  const finalAmount = discountedAmount ?? selectedDetail.amount;

  let walletUsed = 0;
  let cardAmount = finalAmount;

  if (paymentType === 1) {
    // Wallet only
    walletUsed = Math.min(walletBalance, finalAmount);
    cardAmount = 0;
  } else if (paymentType === 3) {
    // Mixed payment
    walletUsed = Math.min(walletBalance, finalAmount);
    cardAmount = Math.max(finalAmount - walletUsed, 0);
  }

  try {
    setLoading(true);
    const subscriptionData = {
      planId: plan.id,
      originalAmount: selectedDetail.amount,
      amount: finalAmount,          //  Always send total plan price
      chefId,
      type: selectedDetail.type,
      validity: selectedDetail.validityCount,
      paymentType,
      walletInputAmount: walletUsed, //  How much wallet is covering
     ...(appliedCoupon?.stripeCouponId && {
  stripeCouponId: appliedCoupon.stripeCouponId,
}),

    };
console.log("Subscription Data:", appliedCoupon?.stripeCouponId, subscriptionData);
    const sessionData = await createCheckoutSession(subscriptionData);

    if (sessionData.status) {
      localStorage.setItem("subscribedPlanId", plan.id);

      //  Wallet-only case (no Stripe needed)
      if (paymentType === 1 && cardAmount === 0) {
        toast.success("Subscription successful using wallet!");
        navigate("/subscription/list");
        return;
      }

      //  Mixed/Card payment → redirect to Stripe
      // Correct way to store sessionId
localStorage.setItem("stripeSessionId", sessionData.id);

      toast.success("Redirecting to payment...");
      return sessionData.id;
    } else {
      throw new Error(sessionData.message || "Failed to create checkout session");
    }
  } catch (error: any) {
    toast.error(error.message || "Failed to process subscription");
    throw error;
  } finally {
    setLoading(false);
  }
};



  const handlePayment = async () => {
    try {
      const sessionId = await handleSubscribe();
      if (sessionId) {
        const stripe = await stripePromise;
        if (!stripe) {
          toast.error("Stripe failed to initialize.");
          return;
        }

        const { error } = await stripe.redirectToCheckout({ sessionId });
        if (error) {
          toast.error("Error redirecting to payment: " + error.message);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="container" style={{ marginTop: "40px", marginBottom: "40px" }}>
      <h2 className="mb-4 text-center fw-bold">Subscription Details</h2>
      <div className="row justify-content-center">
        {/* Left: Package Info */}
        <div className="col-lg-5 col-md-7 mb-4">
          <div className="card p-4 shadow-sm h-100">
            <h5 className="mb-3 fw-semibold">
              Package: <span>{plan.name}</span>
              <span
                className="float-end text-danger"
                style={{ cursor: "pointer" }}
                onClick={() => navigate("/subscription/list")}
              >
                <i className="bi bi-arrow-left"></i> Change Plan
              </span>
            </h5>
            <hr />
            <div className="mb-3">
              <h6 className="fw-semibold">Plan Details:</h6>
              <p className="mb-1">Duration: {selectedDetail.validity}</p>
              <p className="mb-1">Type: {selectedDetail.type === 1 ? "Monthly" : "Yearly"}</p>
              <p className="mb-1">Price: ${originalAmount.toFixed(2)}</p>
            </div>
            <div className="mb-3">
              <h6 className="fw-semibold">Features:</h6>
              <ul className="list-unstyled">
                {features.map((f) => (
                  <li key={f.id} className="d-flex align-items-center my-1">
                    <img
                      src="/assets/images/vector.png"
                      alt="tick"
                      className="me-2"
                      style={{ width: "15px", height: "15px" }}
                    />
                    <span>{f.name}</span>
                  </li>
                ))}
              </ul>
            </div>
            <hr />
            <button
              onClick={() => navigate("/subscription/list")}
              className="btn btn-outline-secondary mt-3 w-100"
            >
              ← Back to Plans
            </button>
          </div>
        </div>

        {/* Right: Payment & Coupon */}
        <div className="col-lg-4 col-md-5 mb-4">
          <div className="card p-4 shadow-sm h-100">
            <h5 className="mb-3 fw-semibold">Bill Details</h5>
            <table className="table table-borderless mb-3">
              <tbody>
                <tr>
                  <td>Original Price</td>
                  <td className="text-end">${originalAmount.toFixed(2)}</td>
                </tr>

                {appliedCoupon && (
                  <tr>
                    <td>
                      Coupon Discount ({appliedCoupon.name})
                      <button
                        type="button"
                        className="btn btn-sm btn-link p-0 ms-1"
                        onClick={handleRemoveCoupon}
                      >
                        <i className="bi bi-x-circle text-danger"></i>
                      </button>
                    </td>
                    <td className="text-end text-success">-${discount.toFixed(2)}</td>
                  </tr>
                )}

                <tr className="border-top">
                  <td className="fw-bold">Total Amount</td>
                  <td className="text-end fw-bold">${total.toFixed(2)}</td>
                </tr>

                {walletBalance > 0 && (
                  <tr>
                    <td>Wallet Balance</td>
                    <td className="text-end">${walletBalance.toFixed(2)}</td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Payment Methods */}
            {walletBalance > 0 && (
              <div className="mb-3">
                <h6 className="fw-semibold mb-2">Payment Method</h6>
                <div className="mb-2">
                  <div className="form-check">
                    <input
                      type="radio"
                      className="form-check-input"
                      id="walletOnly"
                      checked={paymentType === 1}
                      onChange={() => {
  setUserSelectedPayment(true); // mark manual choice
  setPaymentType(1);
}}

                    />
                    <label className="form-check-label" htmlFor="walletOnly">
                      Wallet Only (${walletBalance.toFixed(2)})
                    </label>
                  </div>
                </div>

                {walletBalance < total && (
                  <div className="mb-2">
                    <div className="form-check">
                      <input
                        type="radio"
                        className="form-check-input"
                        id="partialPayment"
                        checked={paymentType === 3}
                        onChange={() => {
  setUserSelectedPayment(true); 
  setPaymentType(3);
}}

                      />
                      <label className="form-check-label" htmlFor="partialPayment">
                        Partial Wallet + Card
                      </label>
                    </div>
                  </div>
                )}

                <div className="mb-2">
                  <div className="form-check">
                    <input
                      type="radio"
                      className="form-check-input"
                      id="cardOnly"
                      checked={paymentType === 2}
                      onChange={() => {
  setUserSelectedPayment(true);
  setPaymentType(2);
}}

                    />
                    <label className="form-check-label" htmlFor="cardOnly">
                      Card Only
                    </label>
                  </div>
                </div>

                {(paymentType === 1 || paymentType === 3) && (
                  <div className="mt-2">
                    <label>Wallet Amount to Use:</label>
                    <input
                      type="number"
                      className="form-control"
                      min={0}
                      max={Math.min(walletBalance, total)}
                      value={walletInputAmount.toFixed(2)}
                     onChange={(e) => {
  let value = parseFloat(e.target.value) || 0;
  value = Math.min(walletBalance, Math.max(0, value));
  setWalletInputAmount(Number(value.toFixed(2)));

  // ⬇ FIX: Only auto-change payment if user has not selected manually
  if (!userSelectedPayment) {
    if (value >= total) setPaymentType(1);
    else if (value > 0) setPaymentType(3);
  }
}}

                    />
                    <small className="text-muted">
                      Maximum: ${Math.min(walletBalance, total).toFixed(2)}
                    </small>
                  </div>
                )}
              </div>
            )}

            {/* Card amount to pay */}
            {paymentType !== 1 && (
              <div className="alert alert-info mb-3">
                <strong>Card Payment: </strong>${(total - walletInputAmount).toFixed(2)}
              </div>
            )}

            {/* Coupon Section */}
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <span>Have a coupon?</span>
                <span
                  style={{ color: "#185abc", cursor: "pointer" }}
                  onClick={() => setShowCouponModal(true)}
                >
                  View All
                </span>
              </div>
              <div className="input-group mt-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Apply Coupon"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                />
                <button
                  className="btn btn-outline-primary"
                  onClick={handleApplyCoupon}
                  disabled={!couponCode.trim()}
                >
                  APPLY
                </button>
              </div>
            </div>

            {/* Payment Button */}
            <button
              className="btn btn-primary w-100 fw-bold"
              style={{ background: "rgb(51, 115, 8)", border: "none", fontSize: "1.05em" }}
              onClick={handlePayment}
              disabled={loading}
            >
              {loading ? "PROCESSING..." : `PAY $${(total - walletInputAmount).toFixed(2)}`}
            </button>
          </div>
        </div>
      </div>

      {/* Coupon Modal */}
      <Modal show={showCouponModal} onHide={() => setShowCouponModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Available Coupons</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {allCoupons.length === 0 && <p>No coupons available.</p>}
          {allCoupons.map((coupon) => {
            const isApplicable =
              coupon.planId === "00000000-0000-0000-0000-000000000000" || coupon.planId === plan.id;
            return (
              <div
                key={coupon.id}
                className={`coupon-item mb-2 p-3 border rounded ${
                  isApplicable ? "bg-light" : "bg-light text-muted"
                }`}
                style={{ cursor: isApplicable ? "pointer" : "not-allowed" }}
                onClick={() => {
                  if (!isApplicable) return;
                  setCouponCode(coupon.name);
setShowCouponModal(false);
setAppliedCoupon(null);
setDiscountedAmount(null);

                }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{coupon.name}</strong>
                    <div>
                      {coupon.couponValueType === 1
                        ? `${coupon.amount}% off`
                        : `$${coupon.amount} off`}
                    </div>
                  </div>
                  {!isApplicable && <span className="badge bg-secondary">Not applicable</span>}
                </div>
              </div>
            );
          })}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SubscriptionDetailsPage;